/**
 * Query Keys Factory
 *
 * Centralized query key management for React Query cache invalidation.
 * Using hierarchical keys enables efficient cache invalidation patterns.
 *
 * Example: Invalidating queryKeys.transactions.all will invalidate all transaction queries
 */

export const queryKeys = {
  // Transactions
  transactions: {
    all: ['transactions'] as const,
    lists: () => [...queryKeys.transactions.all, 'list'] as const,
    list: (params: {
      page: number;
      pageSize: number;
      sortConfig: { column: string; direction: 'asc' | 'desc' };
      filters: {
        search?: string;
        type?: string;
        category?: string | null;
        dateFrom?: string | null;
        dateTo?: string | null;
      };
    }) => [...queryKeys.transactions.lists(), params] as const,
  },

  // Invoices
  invoices: {
    all: ['invoices'] as const,
    lists: () => [...queryKeys.invoices.all, 'list'] as const,
    list: (params: {
      page: number;
      pageSize: number;
      sortConfig: { column: string; direction: 'asc' | 'desc' };
      filters: {
        search?: string;
        status?: string;
        source?: string;
        dateFrom?: string | null;
        dateTo?: string | null;
      };
    }) => [...queryKeys.invoices.lists(), params] as const,
    overdue: (companyId: string) =>
      [...queryKeys.invoices.all, 'overdue', companyId] as const,
  },

  // Bills
  bills: {
    all: ['bills'] as const,
    lists: () => [...queryKeys.bills.all, 'list'] as const,
    list: (params: {
      page: number;
      pageSize: number;
      sortConfig: { column: string; direction: 'asc' | 'desc' };
      filters: {
        search?: string;
        status?: string;
        source?: string;
        dateFrom?: string;
        dateTo?: string;
      };
    }) => [...queryKeys.bills.lists(), params] as const,
    overdue: (companyId: string) =>
      [...queryKeys.bills.all, 'overdue', companyId] as const,
  },

  // Bank Accounts (Cash Balance Summary)
  bankAccounts: {
    all: ['bankAccounts'] as const,
    summary: (companyId: string) =>
      [...queryKeys.bankAccounts.all, 'summary', companyId] as const,
  },

  // Reports
  reports: {
    all: ['reports'] as const,
    weekly: (companyId: string, periodStart: string, periodEnd: string) =>
      [...queryKeys.reports.all, 'weekly', companyId, periodStart, periodEnd] as const,
    latestWeekly: (companyId: string) =>
      [...queryKeys.reports.all, 'weekly', 'latest', companyId] as const,
  },
} as const;
